import { AppEmailVerification } from '@/App'
import {
    LaunchIcon,
    LockIcon,
    MidmarkButton,
    MidmarkTypography,
} from '@midmark-enterprise/shared-components'
import { Box, Grid } from '@mui/material'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

const InfoWithIcon = ({
    icon,
    message,
}: {
    icon: React.ReactNode
    message: string
}) => (
    <Grid
        container
        alignItems="center"
        wrap="nowrap"
        mb={3}
        height={64}
        padding={'16px 0'}
        gap={'12px'}
    >
        <Box
            sx={(theme) => ({
                '& svg': { fontSize: 32, color: '#236192' },
                display: 'flex',
                alignItems: 'center',
                color: theme.palette.text.hint,
                backgroundColor: 'rgba(35, 97, 146, 0.2)',
                width: 56,
                height: 56,
                borderRadius: '50%',
                justifyContent: 'center',
                minWidth: 56,
            })}
        >
            {icon}
        </Box>
        <MidmarkTypography sx={{ flexShrink: 1 }} variant="body1">
            {message}
        </MidmarkTypography>
    </Grid>
)

export default function PasswordSuccess({
    emailVerification,
    redirectUri,
    updateEmailVerification,
}: {
    emailVerification: AppEmailVerification
    redirectUri: string
    updateEmailVerification: (
        id: string | null,
        emailAddress: string,
        code?: string,
        callback?: () => void,
    ) => void
}) {
    const { t } = useTranslation()
    useEffect(() => {
        if (emailVerification.id && emailVerification.emailAddress) {
            updateEmailVerification(null, emailVerification.emailAddress)
        }
    }, [
        emailVerification.emailAddress,
        emailVerification.id,
        updateEmailVerification,
    ])

    return (
        <>
            <MidmarkTypography
                paragraph={true}
                variant="h3"
                marginBottom={3}
                data-testid={DATA_TESTIDS.successTextField}
            >
                {t('password.passwordSuccess.title')}
            </MidmarkTypography>

            <InfoWithIcon
                icon={<LockIcon />}
                message={t(
                    'password.passwordSuccess.infoMessages.newPasswordApplied',
                )}
            />
            <InfoWithIcon
                icon={<LaunchIcon />}
                message={t('password.passwordSuccess.infoMessages.signinNow')}
            />

            <div
                style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    gap: '11px',
                    alignSelf: 'stretch',
                }}
            >
                <MidmarkButton
                    type="submit"
                    data-testid={DATA_TESTIDS.takeMeThereButton}
                    style={{ width: '140px' }}
                    onClick={() => {
                        window.location.href = redirectUri
                    }}
                >
                    {t('password.passwordSuccess.takeMeThereButton')}
                </MidmarkButton>
            </div>
        </>
    )
}

const DATA_TESTIDS = {
    successTextField: 'success-text-field',
    takeMeThereButton: 'take-me-there-button',
}
